import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Grid } from '@material-ui/core';
import { Visibility as VisibilityIcon } from '@material-ui/icons';

import HoveredCard from '../../../common/card/HoveredCard';
import { Task } from '../../tasks/Task.interface';
import TaskFileListCard from '../../tasks/cards/TaskFileListCard';
import SubTaskListCard from '../../tasks/cards/SubTaskListCard';
import TaskStatusVersionCard from '../../tasks/cards/TaskStatusVersionCard';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import SupplierDetailsCard from '../cards/SupplierDetailsCard';
import SupplierReviewDateCard from '../cards/SupplierReviewDateCard';
import SupplierQualityProgramCard from '../cards/SupplierQualityProgramCard';
import SupplierQualityManualCard from '../cards/SupplierQualityManualCard';
import SupplierQualityControlCard from '../cards/SupplierQualityControlCard';
import SupplierQualitySystemCard from '../cards/SupplierQualitySystemCard';
import SupplierInviteCard from '../cards/SupplierInviteCard';
import SupplierFileAttachmentCard from '../cards/SupplierFileAttachmentCard';
import { BaseSupplierCardProps } from '../Supplier.interface';
import TaskJiraIssueAssignCard from '../../tasks/cards/TaskJiraIssueAssignCard';
import environment from '../../../environments';

const SupplierTabInfo: React.FC<BaseSupplierCardProps> = (
  props: BaseSupplierCardProps
) => {
  const { supplier, supplierVersion } = props;
  const supplierCardProps: BaseSupplierCardProps = props;
  const { isFieldDisabled } = useTaskEntityFields(supplier?.task);
  const task = supplierVersion?.task || supplier.task;

  const handleTaskUpdate = (newTask: Partial<Task>) => {
    if (!supplier?.task) {
      return;
    }
    Object.assign(supplier.task, newTask);
    supplierCardProps.onChange?.({});
    supplierCardProps.onUpdate?.();
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SupplierDetailsCard {...supplierCardProps} />
          </Grid>
          <Grid item xs={12}>
            <SupplierQualityProgramCard {...supplierCardProps} />
          </Grid>
          <Grid item xs={12}>
            <SupplierQualityManualCard {...supplierCardProps} />
          </Grid>
          <Grid item xs={12}>
            <SupplierQualityControlCard {...supplierCardProps} />
          </Grid>
          <Grid item xs={12}>
            <SupplierQualitySystemCard {...supplierCardProps} />
          </Grid>
          <Grid item xs={12}>
            <SupplierFileAttachmentCard {...supplierCardProps} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={4}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TaskStatusVersionCard
              taskEntity={supplier}
              renderContent={group =>
                group.version !== supplier.task.version && (
                  <HoveredCard
                    title={`Version ${group.version}`}
                    content='View the previous version'
                    actions={
                      <Button
                        variant='text'
                        color='primary'
                        component={Link}
                        startIcon={<VisibilityIcon />}
                        to={`/portal/suppliers/task/${supplier.task.id}/info?ver=${group.version}`}
                      >
                        View
                      </Button>
                    }
                  />
                )
              }
            />
          </Grid>
          <Grid item xs={12}>
            <SupplierInviteCard {...supplierCardProps} />
          </Grid>
          {task.children && (
            <Grid item xs={12}>
              <SubTaskListCard
                task={task}
                title='Stakeholders'
                disabled={
                  !!supplierVersion || isFieldDisabled('assignSubTasks')
                }
                onError={supplierCardProps.onError}
                onChange={handleTaskUpdate}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TaskFileListCard
              task={supplier.task}
              onError={supplierCardProps.onError}
              onChange={handleTaskUpdate}
            />
          </Grid>
          <Grid item xs={12}>
            <TaskJiraIssueAssignCard
              compact
              task={supplier.task}
              entity={{ title: supplier.companyName }}
              jiraFieldColumns={environment.supplierConfig?.visibleJiraFields}
              onChange={taskJiraIssues => {
                // update supplier Task jira issues on change
                supplier.task.jiraIssues = taskJiraIssues;
                supplierCardProps.onChange?.({ ...supplier });
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <SupplierReviewDateCard {...supplierCardProps} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SupplierTabInfo;
